import { inject, Injectable } from '@angular/core';
import { gql } from '@apollo/client/core';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { FieldTypeEnum } from '@app/core/enums/field-type-enum';
import {
  CreateFieldInput,
  CreateReferenceIndicatorInput,
  DefaultField,
  DefaultFieldInput,
  Field,
  FormatType,
  SuffixType,
  UpdateFieldInput
} from '@app/core/model/other/field-config';
import validatorFieldTypeMapping, {
  ValidatorFieldTypeMap
} from '@app/features/main/views/management/organization/field-configuration/validator-field-type-mapping';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { plainToInstance } from 'class-transformer';
import { DocumentNode } from 'graphql';
import { EMPTY, map, Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class FieldService {
  private createFieldSubject = new Subject<Field>();
  private updateFieldSubject = new Subject<Field>();
  private deleteFieldsSubject = new Subject<Field[]>();

  private readonly fieldGraphqlFragment: DocumentNode = gql`
    fragment FieldInfo on Field {
      id
      code
      label
      tooltip
      entityType
      checkType
      fieldType
      formulaList
      computed
      parentPathList
      fieldValuesList
      validatorsList {
        definition
        type
      }
    }
  `;

  private appManager = inject(AppManager);
  private apiService = inject(ApiService);

  /**
   * Emits any new Field after it was created.
   */
  public get fieldCreated$(): Observable<Field> {
    return this.createFieldSubject.asObservable();
  }

  /**
   * Emits a Field whenever it has been updated.
   */
  public get fieldUpdated$(): Observable<Field> {
    return this.updateFieldSubject.asObservable();
  }

  /**
   * Emits a list of Fields after they have been deleted.
   */
  public get fieldsDeleted$(): Observable<Field[]> {
    return this.deleteFieldsSubject.asObservable();
  }

  /**
   * Query to get a field from an organization
   * @param fieldCode Code of the field to retrieve
   * @param entityType Entity type the field belongs to
   * @param suppressErrorPopup When true, suppresses the global error popup if the field is not found. Defaults to false.
   * @return Observable containing the field, or an error if not found
   */
  public getField(fieldCode: string, entityType: EntityTypeEnum, suppressErrorPopup: boolean = false): Observable<Field> {
    if (!this.appManager.currentOrganization) return EMPTY;

    const query = gql`
      query Field($code: String!, $entityType: EntityType!, $organizationId: String!) {
        field(code: $code, entityType: $entityType, organizationId: $organizationId) {
          ...FieldInfo
        }
      }
      ${this.fieldGraphqlFragment}
    `;
    const variables = {
      code: fieldCode,
      entityType: entityType,
      organizationId: this.appManager.currentOrganization.id
    };

    return this.apiService.query({query, variables, context: {suppressErrorPopup: suppressErrorPopup}})
      .pipe(map(data => plainToInstance(Field, data['field'])));
  }

  /**
   * Query to get every field from an organization
   * @param computedFieldFilter Optional filter on whether the fields are computed or not. Defaults to all fields.
   * @param entityType Optional filter on the entity type of the fields to return
   * @param assetTypes Optional filter on the asset types compatible with the fields to return
   * @param noAssetTypes Optional filter on the absence of asset types with the fields to return
   * @return Observable containing array of fields
   */
  public getFields(
    computedFieldFilter: 'ONLY_COMPUTED' | 'NOT_COMPUTED' | 'ALL' = 'ALL',
    entityType?: EntityTypeEnum,
    assetTypes?: string[],
    noAssetTypes?: boolean
  ): Observable<Field[]> {
    if (!this.appManager.currentOrganization) return EMPTY;

    const query = gql`
      query FieldsByOrganizationId($organizationId: String!, $computed: FieldComputation, $entityType: EntityType, $assetTypes: [String!], $noAssetTypes: Boolean) {
        fields(organizationId: $organizationId, computed: $computed, entityType: $entityType, assetTypes: $assetTypes, noAssetTypes: $noAssetTypes) {
          ...FieldInfo
        }
      }
      ${this.fieldGraphqlFragment}
    `;

    const variables = {
      organizationId: this.appManager.currentOrganization.id,
      computed: computedFieldFilter,
      ...(entityType && {entityType: entityType}),
      ...(assetTypes && {assetTypes: assetTypes}),
      ...(noAssetTypes && {noAssetTypes: noAssetTypes})
    };

    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(Field, data['fields'] as Field[])));
  }

  /**
   * Query to get every available field to an organization that is not already configured for it.
   * These fields might only exist in a classification or for a currency that does not correspond to the organization's
   * @param entityType Filter on the entity type of the fields to return
   * @param computedFieldFilter Optional filter on whether the fields are computed or not. Defaults to all fields.
   * @return Observable containing array of fields
   */
  public getMyaFields(
    entityType: EntityTypeEnum,
    computedFieldFilter: 'ONLY_COMPUTED' | 'NOT_COMPUTED' | 'ALL' = 'ALL'
  ): Observable<DefaultField[]> {
    const query = gql`
      query MyaDefaultFields(
        $entityType: EntityType!,
        $computedFieldFilter: FieldComputation,
        $organizationId: String!
      ) {
        defaultFields(entityType: $entityType, computed: $computedFieldFilter, organizationId: $organizationId) {
          code
          label
          organizationType
          locale
          isTb
          computed
          fieldType
        }
      }
    `;

    const variables = {
      entityType,
      computedFieldFilter,
      organizationId: this.appManager.currentOrganization.id
    };

    return this.apiService.query({query, variables})
      .pipe(map(data => data['defaultFields'] as DefaultField[]));
  }

  /**
   * Query to add an available field to an organization that is not already configured for it
   * These fields might only exist in a classification or for a currency that does not correspond to the organization's
   * @param fieldToAdd the data identifying a field to add to the organization
   * @return Observable of fields
   */
  public addMyaField(fieldToAdd: DefaultFieldInput): Observable<Field> {
    const mutation = gql`
      mutation AddMyaDefaultField($fieldInput: AddDefaultFieldInput!) {
        addDefaultField(fieldInput: $fieldInput) {
          ...FieldInfo
        }
      }
      ${this.fieldGraphqlFragment}
    `;
    const variables = {
      fieldInput: {
        organizationId: this.appManager.currentOrganization.id,
        ...fieldToAdd
      }
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Field, data['addDefaultField'])),
        tap(field => this.createFieldSubject.next(field))
      );
  }

  /**
   * Creates a field given an input containing information about the field itself
   * such as the module it belongs to, its type
   * and other fieldconfig information such as the label and the tooltip
   * @param input Input containing information about the field to be created
   * @return Observable containing the newly created field
   */
  public createField(input: CreateFieldInput): Observable<Field> {
    const mutation = gql`
      mutation CreateField($createFieldInput: CreateFieldInput!) {
        createField(fieldInput: $createFieldInput) {
          ...FieldInfo
        }
      }
      ${this.fieldGraphqlFragment}
    `;
    const variables = {
      createFieldInput: {
        organizationId: this.appManager.currentOrganization.id,
        ...input
      }
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Field, data['createField'])),
        tap(field => this.createFieldSubject.next(field))
      );
  }

  /**
   * Creates a reference indicator given an input containing information about the referenced field
   * and customised data such as the label and the tooltip
   * @param input Input containing information about the referenced field
   * @return Observable containing the newly created reference indicator
   */
  public createReferenceIndicator(input: CreateReferenceIndicatorInput): Observable<Field> {
    const mutation = gql`
      mutation CreateReferenceIndicator($createIndicatorInput: CreateIndicatorInput!) {
        createIndicator(indicatorInput: $createIndicatorInput){
          ...FieldInfo
        }
      }
      ${this.fieldGraphqlFragment}
    `;
    const variables = {
      createIndicatorInput: {
        organizationId: this.appManager.currentOrganization.id,
        ...input
      }
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Field, data['createIndicator'])),
        tap(field => this.createFieldSubject.next(field))
      );
  }


  /**
   * Updates a field given an input of a few field properties
   * @param fieldId Id of the field to update
   * @param input Input containing the new label and the optional empty label and tooltip
   * @return Observable containing the newly updated field
   */
  public updateField(fieldId: string, input: UpdateFieldInput): Observable<Field> {
    const mutation = gql`
      mutation UpdateField($updateFieldInput: UpdateFieldInput!) {
        updateField(fieldInput: $updateFieldInput) {
          ...FieldInfo
        }
      }
      ${this.fieldGraphqlFragment}
    `;
    const variables = {
      updateFieldInput: {id: fieldId, ...input}
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Field, data['updateField'])),
        tap(field => this.updateFieldSubject.next(field))
      );
  }

  /**
   * Deletes a list of fields by organizationId and fieldIds.
   * @param fields Fields to delete
   * @return Observable containing response if the operation was successful
   */
  public deleteFields(fields: Field[]): Observable<boolean> {
    const mutation = gql`
      mutation DeleteFields($fieldIds: [String!]!) {
        deleteFields(ids: $fieldIds)
      }
    `;

    const variables = {
      fieldIds: fields.map(field => field.id)
    };

    return this.apiService.mutate({mutation, variables}).pipe(
      map(data => data['deleteFields'] as boolean),
      tap(success => success && this.deleteFieldsSubject.next(fields))
    );
  }

  /**
   * Fetches the number of existing fields (including computed fields) of an organization for a given entity type
   * @param entityType The entityType to filter by
   * @return Observable containing the number of existing fields
   */
  public getCurrentFieldCount(entityType: EntityTypeEnum): Observable<number> {
    const query = gql`
      query CurrentFieldCount($entityType: EntityType!, $organizationId: String!) {
        currentFieldCount(entityType: $entityType, organizationId: $organizationId)
      }
    `;

    const variables = {
      entityType: entityType,
      organizationId: this.appManager.currentOrganization.id,
    };

    return this.apiService.query({ query, variables}).pipe(
      map(data => data['currentFieldCount'] as number)
    );
  }

  /**
   * Fetches the list of available validators for a given field type, entityType and if the field is unrelated to assetTypes
   * @param fieldType Type of the field
   * @param entityType Entity Type related to the field
   * @param noAssetTypes if the field is unrelated to assetTypes
   * @return Observable containing list of validators
   */
  public getValidators(fieldType: FieldTypeEnum, entityType: EntityTypeEnum, noAssetTypes: boolean): ValidatorFieldTypeMap[] {
    return validatorFieldTypeMapping(fieldType, entityType, noAssetTypes);
  }

  /**
   * Fetches the list of available field types that can be created for an organization.
   * Some other types in the FieldTypeEnum are not allowed as they have a specific use.
   * @return the field types
   */
  public getFieldTypes(): FieldTypeEnum[] {
    return [
      FieldTypeEnum.TEXT,
      FieldTypeEnum.TEXTAREA,
      FieldTypeEnum.NUMERIC,
      FieldTypeEnum.EMAIL,
      FieldTypeEnum.YEAR,
      FieldTypeEnum.LIST,
      FieldTypeEnum.NUMERIC_LIST,
      FieldTypeEnum.TEXT_AUTOCOMPLETE,
      FieldTypeEnum.LIST_MULTIVALUE,
      FieldTypeEnum.SUGGESTBOX,
      FieldTypeEnum.CHIPS,
      FieldTypeEnum.CHIPS_AUTOCOMPLETE,
      FieldTypeEnum.RADIOBUTTON,
      FieldTypeEnum.DATE,
      FieldTypeEnum.DATETIME,
      FieldTypeEnum.LINK
    ];
  }

  /**
   * Returns the list of available format types
   * @return list of format types
   */
  public getFormatTypes(): FormatType[] {
    return [
      'integer',
      'percent',
      'precise_number',
      'numeric',
      'duration',
      'scientific',
      'engineering',
      'currency',
      'engineering_currency'
    ];
  }

  /**
   * Returns the list of available suffix types
   * @return list of suffix types
   */
  public getSuffixTypes(): SuffixType[] {
    return [
      'per_year',
      'excluding_taxes',
      'including_taxes',
      'per_squarefoot',
      'per_squaremeter',
      'per_squaremeter_year',
      'floor',
      'kgeq',
      'kgeqco2_kwh_year',
      'kgeqco2_squaremeter',
      'kgeqco2_squaremeter_year',
      'kmh',
      'kw',
      'kwh',
      'kwhpe',
      'kwhpe_squaremeter',
      'kwhpe_year',
      'kwh_squaremeter',
      'kwh_squaremeter_year',
      'kwh_year',
      'year',
      'month',
      'hour',
      'percent',
      'squarefoot',
      'squaremeter',
      'unit',
      'mm',
      'm',
      'cm',
      'repkman'
    ];
  }
}
